import React, { useState, useEffect } from "react";
import { Dialog, IconButton, Box, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import medalRookie from "../../assets/img/1 APP INTRO & FIRST LAUNCH/medal-rookie-4x.webp";
import medalPro from "../../assets/img/1 APP INTRO & FIRST LAUNCH/medal-pro-4x.webp";
import medalExpert from "../../assets/img/1 APP INTRO & FIRST LAUNCH/medal-expert-4x.webp";

import { useNavigate } from "react-router-dom";
import { getUserInfo } from "../../apiCalls/user_profile/api_v1_profile";
import { setStoredSkillLevel } from "../ui/GaPrimitives";

import {
  trackTestSkillsDialogOpen,
  trackPlayNowClick,
  trackTestSkillsDialogClose,
} from "../../utils/analytics";
import dialogTier from "../../assets/img/1 APP INTRO & FIRST LAUNCH/dialog-tier-ladder.svg";
import {
  CutButton,
  ButtonLabel,
  DialogCard,
  TitleRow,
  TitleWord,
} from "../../features/intro/IntroPrimitives";

const TestSkillsDialog = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [skillLevel, setSkillLevel] = useState("");
  const [lastPlayedLevel, setLastPlayedLevel] = useState(0);
  const [playedGame, setPlayedGame] = useState(false);

  useEffect(() => {
    if (open && skillLevel) {
      trackTestSkillsDialogOpen(skillLevel);
    }
  }, [open, skillLevel]);

  useEffect(() => {
    const storedSkillLevel = sessionStorage.getItem("skillLevel") || "beginner";
    const storedLastLevel =
      parseInt(sessionStorage.getItem("last_played_level"), 10) || 1;

    setSkillLevel(storedSkillLevel);
    setLastPlayedLevel(storedLastLevel);
  }, []);

  // Only hit the network when the dialog is opened (Layout already loads user info).
  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    const fetchUserInfo = async () => {
      try {
        const response = await getUserInfo();
        if (cancelled || response.status !== 200) return;

        const newSkillLevel = response.data.skill_level.toLowerCase();
        const newLastLevel = response.data.latest_played_level;

        setStoredSkillLevel(newSkillLevel);
        sessionStorage.setItem("last_played_level", newLastLevel);
        setSkillLevel(newSkillLevel);
        setLastPlayedLevel(newLastLevel);
      } catch {
        /* keep session values */
      }
    };

    fetchUserInfo();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const calculateProgress = (level) => {
    if (level === "beginner") {
      if (skillLevel === "rookie" || skillLevel === "pro") return 100;

      return Math.min((lastPlayedLevel / 10) * 100, 100);
    }

    if (level === "rookie") {
      if (skillLevel === "pro") return 100;

      if (skillLevel === "beginner") return 0;

      return Math.min(((lastPlayedLevel - 10) / 10) * 100, 100);
    }

    if (level === "pro") {
      if (skillLevel === "beginner" || skillLevel === "rookie") return 0;

      return Math.min(((lastPlayedLevel - 20) / 10) * 100, 100);
    }

    return 0;
  };

  const getMessage = () => {
    switch (skillLevel) {
      case "beginner":
        return "Welcome! Let's start your journey to become a pro!";
      case "rookie":
        return "Great progress! You're on your way to becoming a pro.";
      case "pro":
        return "Almost there! Complete the remaining challenges to become an expert.";
      default:
        return "Welcome to the skill test!";
    }
  };

  const levels = [
    {
      name: "Beginner",
      displayName: "ROOKIE",
      progress: calculateProgress("beginner"),
      icon: medalRookie,
      range: "0 – 250",
      color: "#B8EE3C",
      isCurrent: skillLevel === "beginner",
    },
    {
      name: "Rookie",
      displayName: "PRO",
      progress: calculateProgress("rookie"),
      icon: medalPro,
      range: "250 – 500",
      color: "#F9A825",
      isCurrent: skillLevel === "rookie",
    },
    {
      name: "Pro",
      displayName: "EXPERT",
      progress: calculateProgress("pro"),
      icon: medalExpert,
      range: "500 – 750",
      color: "#9AA88A",
      isCurrent: skillLevel === "pro" || skillLevel === "expert",
    },
  ];

  const handleClose = () => {
    trackTestSkillsDialogClose(skillLevel, playedGame);
    onClose();
  };

  const handlePlayNow = () => {
    trackPlayNowClick(skillLevel);
    setPlayedGame(true);
    onClose();
    navigate("/ga-fps-game");
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          background: "transparent",
          boxShadow: "none",
          overflow: "visible",
          margin: 0,
          width: "100%",
          maxWidth: 478,
          maxHeight: "100vh",
        },
      }}
    >
      <DialogCard
        sx={{
          display: "block",
          width: "calc(100vw - 32px)",
          maxWidth: 360,
          height: "auto",
          maxHeight: "calc(100vh - 32px)",
          margin: "0 auto",
          padding: "clamp(24px, 7vw, 36px) clamp(16px, 5.3vw, 27px) 26px",
          background: "#0A0B08",
          overflow: "hidden",
        }}
      >
        <Box component="img" className="dialog-bg" src={dialogTier} alt="" />
        <IconButton
          onClick={handleClose}
          sx={{
            "&:hover": { backgroundColor: "#15170F" },
            position: "absolute",
            top: 12,
            right: 12,
            border: "1px solid #262B1C",
            color: "#B8EE3C",
            backgroundColor: "#0D0F0A",
            zIndex: 2,
          }}
        >
          <CloseIcon />
        </IconButton>
        <Typography
          sx={{
            fontFamily: "'Rajdhani', sans-serif",
            fontWeight: 700,
            fontSize: "clamp(11px, 3vw, 14px)",
            letterSpacing: "clamp(3px, 1.2vw, 5px)",
            color: "#B8EE3C",
            position: "relative",
            lineHeight: "18px",
            textAlign: "center",
          }}
        >
          TEST YOUR SKILL SET
        </Typography>
        <Box sx={{ height: 8, width: "100%" }} />
        <TitleRow sx={{ gap: "8px" }}>
          <TitleWord
            sx={{
              fontSize: "clamp(21px, 5.8vw, 27px)",
              letterSpacing: "clamp(1px, .5vw, 2px)",
              lineHeight: "35px",
            }}
          >
            UNLOCK
          </TitleWord>
          <TitleWord
            accent
            sx={{
              fontSize: "clamp(21px, 5.8vw, 27px)",
              letterSpacing: "clamp(1px, .5vw, 2px)",
              lineHeight: "35px",
            }}
          >
            NEXT LEVEL
          </TitleWord>
        </TitleRow>
        <Box sx={{ height: 14, width: "100%" }} />
        <Box sx={{ width: "100%", position: "relative", display: "block" }}>
          {levels.map((level, index) => (
            <Box
              key={level.name}
              sx={{
                mb: index === levels.length - 1 ? 0 : "12px",
                width: "100%",
                height: "90px",
                boxSizing: "border-box",
                background:
                  level.isCurrent ? "rgba(184,238,60,0.15)" : "#0D0F0A",
                border:
                  level.isCurrent
                    ? "2px solid #B8EE3C"
                    : "1px solid #262B1C",
                opacity: level.isCurrent || level.progress > 0 ? 1 : 0.45,
                px: "18px",
                pt: "14px",
                pb: "14px",
                display: "block",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  height: "47px",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: "7px" }}>
                  <Box
                    component="img"
                    src={level.icon}
                    alt={level.displayName}
                    sx={{ width: 45, height: 45, objectFit: "contain" }}
                  />
                  <Typography
                    sx={{
                      fontFamily: "'Rajdhani', sans-serif",
                      fontWeight: 700,
                      fontSize: 17,
                      letterSpacing: "1.5px",
                      color: level.color,
                      lineHeight: "22px",
                    }}
                  >
                    {level.displayName}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }} />
                <Typography
                  sx={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: 14,
                    color: "#9AA88A",
                    lineHeight: "20px",
                  }}
                >
                  {level.range}
                </Typography>
              </Box>
              <Box
                sx={{
                  width: "100%",
                  height: 10,
                  mt: "10px",
                  background: "#15170F",
                  border: "1px solid #262B1C",
                  boxSizing: "border-box",
                  position: "relative",
                }}
              >
                {level.progress > 0 && (
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      height: 8,
                      width: `${level.progress}%`,
                      background: "#B8EE3C",
                    }}
                  />
                )}
              </Box>
            </Box>
          ))}
        </Box>
        <Box sx={{ height: 14, width: "100%" }} />
        <CutButton
          wide
          onClick={handlePlayNow}
          sx={{
            position: "relative",
            width: "100%",
            height: "clamp(56px, 15vw, 68px)",
          }}
        >
          <ButtonLabel wide>PLAY NOW</ButtonLabel>
        </CutButton>
      </DialogCard>
    </Dialog>
  );
};

export default TestSkillsDialog;
